import utils from '@happylife-a/utils';

// @INFO: Some real mobile browsers block or throttle localStorage:
//   - iOS Safari in Private Browsing / "Block All Cookies"
//   - In-app webviews (Instagram, Facebook, Telegram, etc.)
//   - Quota exhausted (QuotaExceededError)
// In those environments localStorage.setItem throws, which previously got
// swallowed silently here. The auth tokens then never persisted, so after
// sign-in the user object could not be read back, isLoggedIn never settled to
// true, and the guest route guard left the user on a blank /user/sign-in page.
// This in-memory fallback keeps reads/writes working for the lifetime of the
// session whenever the native storage is unavailable, so auth state survives.
const memoryStore = new Map();

let localStorageAvailable = null;
function isLocalStorageAvailable() {
  if (localStorageAvailable !== null) {
    return localStorageAvailable;
  }

  try {
    const probeKey = '__happylife_storage_probe__';
    localStorage.setItem(probeKey, '1');
    localStorage.removeItem(probeKey);
    localStorageAvailable = true;
  } catch (e) {
    utils.helpers.logging.warning(
      '[StorageDriver] localStorage unavailable, falling back to in-memory storage.'
    );
    utils.helpers.logging.exception(e);
    localStorageAvailable = false;
  }

  return localStorageAvailable;
}

function withLocalStorage(callback, memoryFallback) {
  if (!isLocalStorageAvailable()) {
    return memoryFallback();
  }

  try {
    return callback();
  } catch (e) {
    // @INFO: writes may still throw mid-session (e.g. quota exceeded) even when
    // the initial probe succeeded. Degrade to in-memory rather than losing data.
    utils.helpers.logging.exception(e);
    localStorageAvailable = false;
    return memoryFallback();
  }
}

export async function getItem(key) {
  return withLocalStorage(
    () => {
      const value = localStorage.getItem(key);
      return value === null && memoryStore.has(key) ? memoryStore.get(key) : value;
    },
    () => (memoryStore.has(key) ? memoryStore.get(key) : null)
  );
}

export async function removeItem(key) {
  return withLocalStorage(
    () => {
      memoryStore.delete(key);
      return localStorage.removeItem(key);
    },
    () => memoryStore.delete(key)
  );
}

export async function setItem(key, value) {
  return withLocalStorage(
    () => localStorage.setItem(key, value),
    () => memoryStore.set(key, value)
  );
}

export async function clear() {
  return withLocalStorage(
    () => {
      memoryStore.clear();
      return localStorage.clear();
    },
    () => memoryStore.clear()
  );
}

export async function getAllKeys() {
  if (!isLocalStorageAvailable()) {
    return [...memoryStore.keys()];
  }

  const keys = [];
  for (const key in localStorage) {
    keys.push(key);
  }

  return keys;
}
